import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { demoWorkflowSteps, appliances, users } from '../data/mockData';

const HowItWorks = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSimulating, setIsSimulating] = useState(false);

  const step = demoWorkflowSteps[currentStep];

  const handleNext = () => {
    if (currentStep < demoWorkflowSteps.length - 1) {
      setIsSimulating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsSimulating(false);
      }, 800);
    } else {
      setCurrentStep(0);
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto scrollbar-hide pb-24 md:pb-6">
      <header className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="px-2 py-1 bg-teal-500/10 border border-teal-500/30 rounded text-[10px] font-mono text-teal-400 uppercase tracking-tighter">Interactive Module</div>
        </div>
        <h1 className="text-2xl font-bold text-slate-100">Operation Protocol</h1>
        <p className="text-sm text-slate-400">Experience the HomePulse lifecycle from alert to resolution.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: Progress Track */}
        <div className="lg:col-span-4 space-y-4">
          <div className="glass-panel rounded-2xl p-5 border-l-4 border-l-teal-500">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Workflow Status</h3>
            <div className="space-y-6 relative">
              <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-slate-800" />
              {demoWorkflowSteps.map((s, idx) => (
                <div key={s.id} className={`flex items-start gap-4 relative z-10 transition-opacity duration-300 ${idx > currentStep ? 'opacity-30' : 'opacity-100'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-500
                    ${idx < currentStep ? 'bg-teal-500 border-teal-500 text-slate-900' : 
                      idx === currentStep ? 'bg-slate-900 border-teal-500 text-teal-400 shadow-[0_0_10px_rgba(45,212,191,0.5)]' : 
                      'bg-slate-900 border-slate-700 text-slate-600'}`}>
                    {idx < currentStep ? <SafeIcon icon="FiCheck" /> : <span className="text-xs font-bold">{s.id}</span>}
                  </div>
                  <div>
                    <h4 className={`text-sm font-semibold ${idx === currentStep ? 'text-teal-400' : 'text-slate-300'}`}>{s.title}</h4>
                    {idx === currentStep && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Simulation Area */}
        <div className="lg:col-span-8">
          <div className="glass-panel rounded-2xl overflow-hidden min-h-[400px] flex flex-col">
            <div className="bg-slate-900/50 border-b border-slate-700/50 p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-teal-500/50" />
                </div>
                <span className="text-[10px] font-mono text-slate-500 ml-4">SIMULATION_MODE_V1.0</span>
              </div>
              <div className="text-[10px] font-mono text-teal-500/70 uppercase">Step {currentStep + 1} of 6</div>
            </div>

            <div className="flex-1 p-8 flex items-center justify-center bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-slate-900/40 to-transparent">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 1.05, y: -10 }}
                  className="w-full max-w-md"
                >
                  {isSimulating ? (
                    <div className="text-center py-12">
                      <SafeIcon icon="FiLoader" className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
                      <p className="text-sm font-mono text-slate-400 animate-pulse uppercase tracking-widest">Processing Protocol...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Dynamic Content based on step */}
                      {currentStep === 0 && (
                        <div className="bg-amber-500/10 border border-amber-500/30 p-6 rounded-2xl text-center">
                          <SafeIcon icon="FiActivity" className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                          <h3 className="text-xl font-bold text-amber-400 mb-2">Anomaly Detected</h3>
                          <p className="text-slate-400 text-sm">Vibration sensors in 'Garage' report 24% deviation from baseline.</p>
                        </div>
                      )}

                      {currentStep === 1 && (
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                          <div className="flex justify-between items-center mb-4">
                            <span className="text-xs font-mono text-teal-400">HARDWARE_ID: WM-902</span>
                            <span className="text-xs text-amber-400">Health: 45%</span>
                          </div>
                          <div className="space-y-4">
                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-500 w-[45%]" />
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-[10px] font-mono text-slate-500">
                              <div className="p-2 bg-slate-900 rounded">TEMP: 104°F</div>
                              <div className="p-2 bg-slate-900 rounded">RPM: 1200 [STABLE]</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {currentStep === 2 && (
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl text-center">
                           <div className="flex justify-center -space-x-3 mb-6">
                              {users.map(u => (
                                <img key={u.id} src={u.avatar} className="w-12 h-12 rounded-full border-2 border-slate-900" alt={u.name} />
                              ))}
                           </div>
                           <h3 className="text-lg font-bold text-white mb-2">Smart Allocation</h3>
                           <p className="text-sm text-slate-400 mb-4">Alex has the highest 'Hardware' skill rating and is currently free.</p>
                           <div className="py-2 px-4 bg-teal-500/10 text-teal-400 text-xs rounded-full inline-block">Assigned to: Alex</div>
                        </div>
                      )}

                      {currentStep === 3 && (
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl">
                           <h4 className="text-xs font-bold text-slate-500 uppercase mb-4">Inventory Scan</h4>
                           <div className="space-y-3">
                              {[
                                { name: 'Synthetic Grease', status: 'In Stock', icon: 'FiDroplet' },
                                { name: 'Bearing Puller', status: 'In Stock', icon: 'FiTool' },
                                { name: 'Replacement Seal', status: 'Out of Stock', icon: 'FiX' }
                              ].map((item, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-slate-900 rounded">
                                  <div className="flex items-center gap-2">
                                    <SafeIcon icon={item.icon} className={item.status === 'In Stock' ? 'text-teal-500' : 'text-red-500'} />
                                    <span className="text-xs text-slate-300">{item.name}</span>
                                  </div>
                                  <span className={`text-[10px] ${item.status === 'In Stock' ? 'text-teal-400' : 'text-red-400'}`}>{item.status}</span>
                                </div>
                              ))}
                           </div>
                        </div>
                      )}

                      {currentStep === 4 && (
                        <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl text-center">
                           <div className="w-24 h-24 bg-slate-900 border border-slate-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
                              <SafeIcon icon="FiCamera" className="w-8 h-8 text-slate-500" />
                           </div>
                           <p className="text-sm text-slate-300">Maintenance photo captured</p>
                           <p className="text-[10px] text-slate-500 mt-1 font-mono">IMG_4920_HW.JPG // 2.4MB</p>
                        </div>
                      )}

                      {currentStep === 5 && (
                        <div className="bg-teal-500/10 border border-teal-500/30 p-8 rounded-2xl text-center">
                          <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-[0_0_20px_rgba(45,212,191,0.4)]">
                            <SafeIcon icon="FiCheck" className="w-10 h-10 text-slate-900" />
                          </div>
                          <h3 className="text-2xl font-bold text-teal-400 mb-2">Protocol Complete</h3>
                          <p className="text-slate-400 text-sm">All systems returned to nominal. HomePulse is once again vigilant.</p>
                        </div>
                      )}

                      <button 
                        onClick={handleNext}
                        className="w-full py-4 bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(45,212,191,0.3)] mt-8"
                      >
                        {currentStep === demoWorkflowSteps.length - 1 ? 'Restart Demo' : step.action}
                        <SafeIcon icon={currentStep === demoWorkflowSteps.length - 1 ? 'FiRefreshCw' : 'FiArrowRight'} />
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            
            <div className="p-4 bg-slate-900/30 text-center border-t border-slate-800/50">
               <p className="text-[10px] text-slate-500 italic">This is a simulated workflow. No actual hardware is being modified.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center border-t border-slate-800 pt-6">
        <p className="text-sm text-slate-400">Made by LingLink AI and DigLab AI</p>
      </div>
    </div>
  );
};

export default HowItWorks;